import React, { useState, useMemo } from 'react';
import { useContent } from '../../context/ContentContext';
import { formatRupiah } from '../../utils/formatters';
import { resolveMediaUrl } from '../../utils/api';
import { Search, MapPin, ChevronDown, Plus, Coffee, ArrowLeft } from 'lucide-react';
import { MenuItem } from '../../types';

interface MenuSectionProps {
  onOpenOrder?: (item?: MenuItem) => void;
  onBack?: () => void;
}

const DEFAULT_PRODUCT_IMAGE =
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1000&q=80';

export const MenuSection: React.FC<MenuSectionProps> = ({ onOpenOrder, onBack }) => {
  const { data } = useContent();
  const { menuCategories, menuItems } = data;
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const sortedCategories = useMemo(() => {
    return [...(menuCategories || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuCategories]);

  const availableItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (menuItems || [])
      .filter((item) => {
        if (!item || item.isAvailable === false) return false;
        const itemName = (item.name || '').toLowerCase();
        const itemDesc = (item.description || '').toLowerCase();
        return !q || itemName.includes(q) || itemDesc.includes(q);
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [menuItems, searchQuery]);

  const sectionsToRender = useMemo(() => {
    if (selectedCategory !== 'all') {
      const cat = sortedCategories.find((c) => c.id === selectedCategory);
      const items = availableItems.filter((i) => i.categoryId === selectedCategory);
      return cat ? [{ id: cat.id, name: cat.name, items }] : [];
    }
    const grouped = sortedCategories
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        items: availableItems.filter((i) => i.categoryId === cat.id),
      }))
      .filter((sec) => sec.items.length > 0);

    if (grouped.length > 0) return grouped;
    return availableItems.length > 0
      ? [{ id: 'all', name: 'Signature Menu', items: availableItems }]
      : [];
  }, [selectedCategory, sortedCategories, availableItems]);

  const selectedOutletLabel = useMemo(() => {
    try {
      const saved = localStorage.getItem('leton_selected_outlet');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.name) {
          return parsed.name.replace(/^Leton\s+/i, '');
        }
      }
    } catch {}
    return 'Sudirman Central';
  }, []);

  return (
    <div id="menu" className="bg-white text-[#0A192F] pb-32 lg:pb-16 max-w-lg md:max-w-3xl lg:max-w-7xl mx-auto min-h-screen">
      {/* Header — Exact UX Pilot Menu Structure */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0] px-4 lg:px-8 py-3 lg:py-4">
        <div className="flex justify-between items-center mb-3 gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Kembali"
                title="Kembali"
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-[#0A192F] cursor-pointer hover:border-[#0056D2]/40 transition-colors shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <h1 className="text-xl font-bold text-[#0A192F] truncate">Leton Menu</h1>
          </div>
          <button
            type="button"
            onClick={() => onOpenOrder && onOpenOrder()}
            className="flex items-center gap-1 bg-[#F8FAFC] px-3 py-1.5 rounded-full border border-[#E2E8F0] cursor-pointer"
          >
            <MapPin className="w-3 h-3 text-[#0056D2]" />
            <span className="text-[11px] font-bold text-[#0A192F]">{selectedOutletLabel}</span>
            <ChevronDown className="w-2.5 h-2.5 text-[#0A192F]" />
          </button>
        </div>

        {/* Search & Categories */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#F8FAFC] border-none rounded-xl py-2.5 pl-10 pr-4 text-base sm:text-sm text-[#0A192F] placeholder:text-slate-400 focus:ring-2 focus:ring-[#0056D2]/20 transition-all outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-[#0A192F] text-white font-bold'
                : 'bg-[#F8FAFC] text-slate-500 border border-[#E2E8F0] font-semibold'
            }`}
          >
            Coffee &amp; All
          </button>

          {sortedCategories.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-[#0A192F] text-white font-bold'
                    : 'bg-[#F8FAFC] text-slate-500 border border-[#E2E8F0] font-semibold'
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </header>

      <main className="px-4 lg:px-8 mt-6">
        {sectionsToRender.length === 0 ? (
          <div className="py-16 text-center rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0]">
            <Coffee className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-[#0A192F] font-bold text-base">Menu tidak ditemukan</p>
            <p className="text-xs text-slate-500 mt-1">
              Coba kata kunci lain atau pilih kategori berbeda.
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="mt-4 px-4 py-2 rounded-xl bg-[#0056D2] text-white text-xs font-bold cursor-pointer"
            >
              Reset Filter
            </button>
          </div>
        ) : (
          sectionsToRender.map((sec) => (
            <section key={sec.id} className="mb-8">
              <h2 className="font-bold text-base mb-4 flex items-center gap-2 text-[#0A192F]">
                <span>{sec.name}</span>
                <span className="h-[1px] flex-1 bg-[#E2E8F0]"></span>
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                {sec.items.map((item) => {
                  const resolvedImg = resolveMediaUrl(item.image) || DEFAULT_PRODUCT_IMAGE;

                  return (
                    <div
                      key={item.id}
                      onClick={() => onOpenOrder && onOpenOrder(item)}
                      className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="h-44 lg:h-52 bg-[#F8FAFC] relative overflow-hidden">
                        <img
                          className="w-full h-full object-cover"
                          src={resolvedImg}
                          alt={item.name}
                          loading="lazy"
                          onError={(e) => {
                            const target = e.currentTarget;
                            if (target.src !== DEFAULT_PRODUCT_IMAGE) {
                              target.src = DEFAULT_PRODUCT_IMAGE;
                            }
                          }}
                        />
                        {item.badge && (
                          <div className="absolute top-2 left-2 bg-[#0056D2] text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase">
                            {item.badge}
                          </div>
                        )}
                      </div>
                      <div className="p-3 flex-1 flex flex-col justify-between">
                        <div>
                          <h3 className="font-bold text-sm leading-tight mb-1 text-[#0A192F]">
                            {item.name}
                          </h3>
                          <p className="text-[10px] text-slate-500 line-clamp-2">
                            {item.description || 'Smooth espresso with silky milk'}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="font-bold text-[#0A192F] text-sm">
                            {formatRupiah(item.price)}
                          </span>
                          <button
                            type="button"
                            id={`order-online-btn-${item.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onOpenOrder) onOpenOrder(item);
                            }}
                            aria-label={`Pesan ${item.name}`}
                            className="w-8 h-8 bg-[#0056D2] text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
};
